'use client';
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { User, Session } from '@supabase/supabase-js';

interface UserProfile {
  id: string
  name?: string
  email?: string
  fireflies_api_key?: string
  [key: string]: any
}

interface AuthContextType {
  user: User | null
  userProfile: UserProfile | null
  loading: boolean
  profileLoading: boolean
  signIn: (email: string, password: string) => Promise<any>
  signOut: () => Promise<any>
  updateProfile: (updates: Partial<UserProfile>) => Promise<any>
  isAuthenticated: boolean
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  const profileOperations = {
    async load(userId: string) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .single()
        
        if (error) {
          console.error('Profile load error:', error)
          // If profile doesn't exist, create one
          if (error.code === 'PGRST116') {
            const { data: newProfile, error: createError } = await supabase
              .from('user_profiles')
              .insert({
                id: userId,
                email: user?.email || null,
                full_name: user?.user_metadata?.full_name || null
              })
              .select()
              .single()
            
            if (!createError && newProfile) {
              setUserProfile(newProfile)
            }
          }
        } else {
          setUserProfile(data)
        }
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },
    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    },
    async refresh(userId: string) {
      await this.load(userId)
    }
  }

  const authStateHandlers = {
    onChange: (_event: string | null, session: Session | null) => {
      setUser(session?.user ?? null)
      setLoading(false)
      if (session?.user) {
        profileOperations.load(session.user.id)
      } else {
        profileOperations.clear()
      }
    },
  }

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      authStateHandlers.onChange(null, session)
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(authStateHandlers.onChange)

    return () => subscription?.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      return { data, error }
    } catch {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    // Try client-local sign out first
    let signOutError: any = null
    try {
      await supabase.auth.signOut({ scope: 'local' as any })
    } catch (e) {
      signOutError = e
    }

    // Proactively clear any cached tokens in localStorage (handles cases where /logout 403s)
    try {
      const url = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
      let projectRef = ''
      try { projectRef = new URL(url).host.split('.')[0] || '' } catch {}
      const keys = [
        'supabase.auth.token',
        projectRef ? `sb-${projectRef}-auth-token` : '',
        projectRef ? `sb-${projectRef}-auth-token.0` : '',
        projectRef ? `sb-${projectRef}-auth-token.1` : '',
      ].filter(Boolean)
      keys.forEach((k) => {
        try { localStorage.removeItem(k) } catch {}
      })
    } catch {}

    // Reset local state regardless of server response
    setUser(null)
    profileOperations.clear()
    return { error: signOutError }
  }

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return { error: { message: 'No user logged in' } }
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single()
      if (!error) {
        setUserProfile(data)
      }
      return { data, error }
    } catch {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const refreshProfile = async () => {
    if (user?.id) {
      await profileOperations.refresh(user.id)
    }
  }

  const value: AuthContextType = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
    refreshProfile,
  }

  return <AuthContext.Provider data-component-id="src/contexts/AuthContext.tsx:189:9" data-component-path="src/contexts/AuthContext.tsx" data-component-line="189" data-component-file="AuthContext.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>{children}</AuthContext.Provider>
}
